import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { legalDocs } from '../data/legalContent';
import Footer from '../components/Footer';
import PageSeo from '../seo/PageSeo';

export default function LegalPage({ docKey }) {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, docKey]);

  const doc = legalDocs[docKey];
  if (!doc) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Document not found.</p>
        <Link to="/">Home</Link>
      </div>
    );
  }

  const firstSection = doc.sections[0];
  const firstPara =
    (firstSection?.paragraphs && firstSection.paragraphs[0]) || firstSection?.p || '';
  const seoDescription = firstPara || `${doc.title} for ChooseRightCareer.`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/80 to-white flex flex-col">
      <PageSeo
        title={`${doc.title} | ChooseRightCareer`}
        description={seoDescription}
        path={location.pathname}
      />
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-blue-100 shadow-sm">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-blue-700 font-semibold hover:text-teal-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to home
          </Link>
          <Link to="/" className="font-display text-xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
            ChooseRightCareer
          </Link>
        </div>
      </header>

      <article className="flex-1 container mx-auto px-6 py-12 max-w-3xl">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mb-2 tracking-tight">
          {doc.title}
        </h1>
        <p className="text-sm text-gray-500 mb-10">Last updated: {doc.updated}</p>

        <div className="prose prose-gray max-w-none space-y-10">
          {doc.sections.map((s) => {
            const paras = s.paragraphs ?? (s.p ? [s.p] : []);
            return (
              <section key={s.h}>
                <h2 className="font-display text-xl font-bold text-gray-900 mb-3">{s.h}</h2>
                <div className="space-y-4">
                  {paras.map((para, i) => (
                    <p key={i} className="text-gray-600 leading-relaxed text-[15px] md:text-base">
                      {para}
                    </p>
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        <p className="mt-12 text-xs text-gray-400 border-t border-gray-200 pt-6">
          These pages describe how we intend to operate and are not personal legal advice. Have Indian counsel review
          the final text before you rely on it in tenders, parent notices, or disputes.
        </p>
      </article>

      <Footer />
    </div>
  );
}
